import { useEffect, useState } from "react";
import styled from "styled-components";
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/splide/dist/css/splide.min.css';
import { Link } from "react-router-dom";
import { FiClock } from 'react-icons/fi';
import ShimmerCard from "./ShimmerCard";
import cacheManager from '../utils/cacheManager';
import { filterRecipesWithImages } from '../utils/recipeUtils';

// Recipe slide that hides itself entirely if image fails to load
function RecipeSlide({ recipe }) {
  const [imageError, setImageError] = useState(false);

  if (imageError) return null;

  return (
    <SplideSlide className="popularSlide">
      <Card>
        <CardLink to={"/recipe/" + recipe.id}>
          <ImageWrapper>
            <img
              src={recipe.image}
              alt={recipe.title}
              onError={() => setImageError(true)}
            />
            <Gradient />
          </ImageWrapper>
          <CardContent>
            <CardTitle>{recipe.title}</CardTitle>
            <CardMeta>
              {recipe.readyInMinutes > 0 && (
                <MetaBadge>
                  <MetaIcon><FiClock /></MetaIcon>
                  {recipe.readyInMinutes}m
                </MetaBadge>
              )}
              {recipe.spoonacularScore != null && (
                <MetaBadge>
                  <MetaIcon>⭐</MetaIcon>
                  {Math.round(recipe.spoonacularScore)}
                </MetaBadge>
              )}
            </CardMeta>
          </CardContent>
        </CardLink>
      </Card>
    </SplideSlide>
  );
}

function Popular() {

  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPopular();
  },[]);


  const getPopular = async () => {
    setLoading(true);

    // Check cache first
    const cached = await cacheManager.get('popular', 'POPULAR');
    if (cached) {
      setPopular(cached);
      setLoading(false);
      return;
    }

    // Fetch from API (request more to account for filtering)
    try {
      // Request more recipes since we'll filter by score and image
      const api = await fetch(`https://api.spoonacular.com/recipes/random?apiKey=${process.env.REACT_APP_API_KEY}&number=100`);
      const data = await api.json();
      // Filter out recipes without valid images AND require score >= 70
      const recipes = filterRecipesWithImages(data.recipes || [])
        .filter(recipe => recipe.spoonacularScore >= 70)
        .slice(0, 21);

      // Cache the results (7 day TTL)
      await cacheManager.set('popular', recipes, 'POPULAR');

      setPopular(recipes);
    } catch (error) {
      console.error('Error fetching popular recipes:', error);
      setPopular([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Wrapper>
        <SectionTitle>Popular Picks</SectionTitle>
        <Splide
          options={{
            type: 'slide',
            perPage: 4,
            perMove: 4,
            arrows: true,
            pagination: false,
            drag: true,
            wheel: false,
            autoplay: !loading,
            interval: 4000,
            pauseOnHover: true,
            pauseOnFocus: true,
            resetProgress: false,
            gap: "1.5rem",
            speed: 600,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            breakpoints: {
              1300: {
                perPage: 3,
                perMove: 3,
              },
              900: {
                perPage: 2,
                perMove: 2,
                gap: "1rem",
                arrows: false,
              },
              600: {
                perPage: 1,
                perMove: 1,
                arrows: false,
              },
            },
          }}
        >
          {loading ? (
            Array.from({ length: 8 }).map((_, index) => (
              <SplideSlide className="popularSlide" key={`shimmer-${index}`}>
                <ShimmerCard />
              </SplideSlide>
            ))
          ) : (
            popular.map((recipe) => (
              <RecipeSlide key={recipe.id} recipe={recipe} />
            ))
          )}
        </Splide>
      </Wrapper>
    </div>
  )
}

const Wrapper = styled.div`
  margin: var(--spacing-lg) 0;
  position: relative;
  padding: 0 var(--spacing-sm);
  cursor: grab;
  overflow: hidden;
  width: 100%;

  &:active {
    cursor: grabbing;
  }

  /* Subtle fade hint for scrollability */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40px;
    pointer-events: none;
    z-index: 2;
    transition: opacity var(--transition-base);
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--color-background), transparent);
    opacity: 0;
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--color-background), transparent);
  }

  @media screen and (max-width: 900px) {
    margin: var(--spacing-sm) 0;
    padding: 0;

    &::before,
    &::after {
      display: none;
    }
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-sm);
  text-align: left;
  letter-spacing: -0.01em;

  @media screen and (max-width: 900px) {
    font-size: 1.125rem;
    text-align: left;
    margin-bottom: var(--spacing-xs);
    padding-left: var(--spacing-xs);
  }
`;

const Card = styled.div`
  border-radius: var(--radius-md);
  overflow: hidden;
  position: relative;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border-subtle);
  transition: transform var(--transition-fast), background var(--transition-fast), border-color var(--transition-fast), box-shadow var(--transition-fast);
  height: 100%;

  &:hover {
    transform: translateY(-3px);
    border-color: var(--color-border);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }
`;

const CardLink = styled(Link)`
  display: block;
  text-decoration: none;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 66.67%;
  overflow: hidden;
  background: var(--color-border);

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CardContent = styled.div`
  padding: var(--spacing-sm);
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 70px;
`;

const CardTitle = styled.p`
  color: var(--color-text-primary);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;

  @media screen and (max-width: 900px) {
    font-size: 0.875rem;
  }
`;

const CardMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto;
`;

const MetaBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-normal);
`;

const MetaIcon = styled.span`
  font-size: 0.75rem;
  display: flex;
  align-items: center;
`;

const Gradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40%;
  background: linear-gradient(to top, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%);
  z-index: 1;
  pointer-events: none;
`

export default Popular
